import {
  centroid,
  normalizePoint,
  seedShapePoints,
} from 'features/Responder/features/Zones';

/**
 * Normalize geometry points into {lat,lng} objects.
 * @param {object} geometry
 * @returns {Array<{lat: number, lng: number}>}
 */
const normalizeGeometryPoints = (geometry) => {
  if (!geometry) return [];
  const raw = geometry.points ?? geometry.coord ?? geometry.polygon ?? [];
  return Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
};

/**
 * Check if the stop represents a zone-like geometry.
 * @param {object} stop
 * @returns {boolean}
 */
export const isZoneGeometry = (stop) => {
  const geometry = stop?.geometry || {};
  const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
  if (geometry?.isZone || type === 'zone' || type === 'polygon') return true;
  if (type === 'circle') {
    const radius = Number(
      geometry?.radiusMeters ?? stop?.radiusMeters ?? geometry?.radius ?? stop?.radius,
    );
    if (Number.isFinite(radius) && radius > 0) return true;
    if (geometry?.zoneId != null || stop?.zoneId != null) return true;
  }
  return false;
};

/**
 * Resolve a stop to a lat/lng (from coordinates or geometry centroid).
 * @param {object} stop
 * @returns {{lat: number, lng: number}|null}
 */
const getStopLatLng = (stop) => {
  if (!stop) return null;
  const lat = Number(stop.lat ?? stop.latitude ?? stop.coordinates?.[0] ?? stop.coords?.[0]);
  const lng = Number(stop.lng ?? stop.longitude ?? stop.coordinates?.[1] ?? stop.coords?.[1]);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const points = normalizeGeometryPoints(stop?.geometry);
  if (points.length) return centroid(points) || points[0];
  return null;
};

/**
 * Resolve circle radius in meters with a default fallback.
 * @param {object} stop
 * @returns {number}
 */
const getCircleRadiusMeters = (stop) => {
  const radius = Number(
    stop?.geometry?.radiusMeters ?? stop?.radiusMeters ?? stop?.geometry?.radius ?? stop?.radius,
  );
  return Number.isFinite(radius) && radius > 0 ? radius : 75;
};

/**
 * Resolve zone polygon points, seeding a circle when required.
 * @param {object} stop
 * @returns {Array<{lat: number, lng: number}>}
 */
export const getZonePolygonPoints = (stop) => {
  const geometry = stop?.geometry || {};
  const points = normalizeGeometryPoints(geometry);
  if (points.length >= 3) return points;
  const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
  if (geometry?.isZone || type === 'zone' || type === 'circle') {
    const center = getStopLatLng(stop);
    if (center) {
      return seedShapePoints(center, 'circle', getCircleRadiusMeters(stop));
    }
  }
  return points;
};

/**
 * Resolve a stop into a routing payload (lat/lng or placeId).
 * @param {object} stop
 * @returns {Promise<{lat?: number, lng?: number, placeId?: string}|null>}
 */
export const resolveRouteStop = async (stop) => {
  if (!stop) return null;
  if (isZoneGeometry(stop)) {
    const latLng = getStopLatLng(stop);
    if (latLng) {
      return { lat: latLng.lat, lng: latLng.lng, placeId: '' };
    }
  }
  if (stop.placeId) return { placeId: stop.placeId };
  const latLng = getStopLatLng(stop);
  return latLng ? { lat: latLng.lat, lng: latLng.lng } : null;
};


